import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Landmark } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TAX_OFFICES } from "@/data/taxOffices";

const lc = (s) => String(s || "").toLocaleLowerCase("tr");

// Resmi Türkiye vergi daireleri için aranabilir açılır liste. Listede olmayan değer de yazılabilir (allowCustom).
export function TaxOfficeSelect({ value, onChange, placeholder = "Vergi dairesi seçin / arayın", className, disabled, ...props }) {
  const tid = props["data-testid"];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = lc(query).trim();
  const filtered = useMemo(() => {
    if (!q) return TAX_OFFICES.slice(0, 80);
    const out = [];
    for (const o of TAX_OFFICES) {
      if (lc(o.n).includes(q) || lc(o.c).includes(q) || lc(o.d).includes(q)) {
        out.push(o);
        if (out.length >= 80) break;
      }
    }
    return out;
  }, [q]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={tid}
          className={cn("w-full justify-between font-normal h-9 bg-white", !value && "text-muted-foreground", className)}>
          <span className="truncate flex items-center gap-1.5 min-w-0"><Landmark className="h-3.5 w-3.5 shrink-0 opacity-60" /><span className="truncate">{value || placeholder}</span></span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[300px]" align="start" data-testid={tid ? `${tid}-content` : undefined}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Vergi dairesi / il / ilçe ara..." value={query} onValueChange={setQuery} data-testid={tid ? `${tid}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>
              {query.trim() ? (
                <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent" data-testid={tid ? `${tid}-custom` : undefined}
                  onClick={() => { onChange(query.trim()); setOpen(false); setQuery(""); }}>&ldquo;{query.trim()}&rdquo; olarak kullan</button>
              ) : "Aramak için yazın"}
            </CommandEmpty>
            <CommandGroup>
              {query.trim() && (
                <CommandItem value="__use_typed" onSelect={() => { onChange(query.trim()); setOpen(false); setQuery(""); }} data-testid={tid ? `${tid}-use-typed` : undefined}>
                  <span className="text-xs text-muted-foreground">Yazdığınızı kullan: &ldquo;{query.trim()}&rdquo;</span>
                </CommandItem>
              )}
              {filtered.map((o, idx) => (
                <CommandItem key={`${o.n}-${idx}`} value={`${o.n}__${idx}`} data-testid={tid ? `${tid}-item-${idx}` : undefined}
                  onSelect={() => { onChange(o.n); setOpen(false); setQuery(""); }}>
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.n ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{o.n}</span>
                    <span className="block text-[10px] text-muted-foreground truncate">{o.c} · {o.d}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default TaxOfficeSelect;
